import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController, NavController } from '@ionic/angular';
import _ from 'lodash';

import { delay } from 'src/app/utils/utils';
import { Organizer } from 'src/models';
import { Project } from 'src/models/projects';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';

@Component({
  selector: 'app-customer-create',
  templateUrl: './customer-create.page.html',
  styleUrls: ['./customer-create.page.scss'],
})
export class CustomerCreatePage implements OnInit {
  isLoading = false;
  org: Organizer = new Organizer();
  channel: any = null;
  searchText: string;
  unconnectedChannels: any[] = [];
  userChannels: any[] = [];
  customerChannels: Set<string>;
  selectedChannels: any[] = [];
  filteredChannels: any[] = [];
  isLoadingChannels = false;
  userConnectedToSlack = false;
  teamConnectedToSlack = false;
  isFilteringSharedChannels = true;
  showHelpBanner = false;
  internalChannelOverride = false;
  isConnecting = false;
  teamScopes: any;
  connectedTeams: any[] = [];
  selectedTeam?: any;
  isSlackDisconnected = false;
  project: Partial<Project>;

  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private navController: NavController,
    private loadingController: LoadingController,
    private slackService: SlackService,
    private searchablePopoverService: SearchablePopoverService,
    private customersService: CustomersService,
    private router: Router,
  ) {}

  async ngOnInit() {
    if (this.authService.tenant.accountsEnabled && this.router.url === '/dashboard/customers/new') {
      // redirect to channels list page instead
      this.router.navigate([`/dashboard/channels/new`]);
      return;
    }

    this.isLoading = true;

    if (this.authService.selectedProject) {
      this.setProject(this.authService.selectedProject);
    } else if (this.authService.projects?.length) {
      this.setProject(this.authService.projects[0]);
    }

    this.connectedTeams = await this.slackService.getConnectedTeams();
    this.selectedTeam = this.connectedTeams[0];

    await this.loadChannels();

    this.isLoading = false;
  }

  setProject(project: Partial<Project>) {
    this.project = project;
    this.internalChannelOverride = !this.project.isExternal;
  }

  async loadChannels() {
    this.isLoadingChannels = true;

    if (this.selectedTeam) {
      const [teamScopes, userScopes] = await Promise.all([
        this.slackService.getScopes(this.selectedTeam.connectionId),
        this.slackService.getUserScopes(this.selectedTeam.connectionId),
      ]);

      this.teamScopes = teamScopes;
      this.teamConnectedToSlack = teamScopes.exists;
      this.userConnectedToSlack =
        userScopes.exists && !(await this.slackService.requiresUserScope(undefined, this.selectedTeam.connectionId));

      if (this.teamConnectedToSlack) {
        await Promise.all([this.loadUserChannels(), this.loadCustomerChannels()]);

        let hasConnectChannels = false;
        this.unconnectedChannels = this.userChannels.filter((channel) => {
          const isUnconnected = !this.customerChannels.has(channel.id);

          if (channel.isShared && isUnconnected) {
            hasConnectChannels = true;
          }

          return isUnconnected;
        });

        // TODO: Base this on the project isExternal setting instead.
        if (!hasConnectChannels || this.authService.tenant.isInternalSupport) {
          this.isFilteringSharedChannels = false;
          this.internalChannelOverride = true;
        }

        this.showHelpBanner = !this.userChannels.length;

        this.handleFilter();
      }
    }

    this.isLoadingChannels = false;
  }

  async loadUserChannels() {
    try {
      if (this.userConnectedToSlack) {
        this.userChannels = await this.slackService.getChannelsForUser(
          undefined,
          this.selectedTeam.connectionId,
          this.selectedTeam.id,
        );
      } else {
        const promises = [
          this.slackService.getConnectedChannels(true, this.selectedTeam.connectionId, this.selectedTeam.id),
        ];

        if (this.teamScopes.scopes['channels:join']) {
          promises.push(
            this.slackService.getPublicChannels(undefined, this.selectedTeam.connectionId, this.selectedTeam.id),
          );
        } else {
          promises.push(Promise.resolve([]));
        }

        const [connectedChannels, publicChannels] = await Promise.all([
          this.slackService.getConnectedChannels(true, this.selectedTeam.connectionId, this.selectedTeam.id),
          this.slackService.getPublicChannels(true, this.selectedTeam.connectionId, this.selectedTeam.id),
        ]);

        this.userChannels = _.unionBy(connectedChannels, publicChannels, 'id');
      }
    } catch (e) {
      if (e.status == 500) {
        this.isSlackDisconnected = true;
      } else {
        this.msgSrvc.showError(e);
      }
    }
  }

  async loadCustomerChannels() {
    const customers = await this.customersService.list({
      limit: null,
      order: ['slackChannelId'],
      descending: false,
    });

    this.customerChannels = new Set(customers.data.map((customer) => customer.slackChannelId));
  }

  deselectAll() {
    this.selectedChannels = [];
  }

  selectAll() {
    this.selectedChannels = [...this.filteredChannels];
  }

  async save() {
    if (!this.selectedChannels?.length) {
      this.msgSrvc.show('Please select channels by ticking the checkboxes');
      return;
    }

    if (!this.project?.id) {
      this.msgSrvc.show('Please select a project');
      return;
    }

    this.isLoading = true;

    try {
      const loading = await this.loadingController.create({
        message: 'Connecting channels...',
      });

      await loading.present();

      await this.customersService.bulkCreate(
        this.selectedChannels.reduce((acc, c) => {
          acc[c.id] = c.name;

          return acc;
        }, {}),
        !this.isFilteringSharedChannels && this.internalChannelOverride,
        this.selectedTeam.connectionId,
        this.project?.id,
      );

      await delay(3000);

      if (this.selectedChannels.length === 1) {
        // Try to find the one channel that was connected
        const customers = await this.customersService.list({
          limit: 1,
          order: ['slackChannelId'],
          descending: false,
          where: [
            {
              field: 'slackChannelId',
              operator: '==',
              value: this.selectedChannels[0].id,
            },
          ],
        });

        // If customer channels contains the slack channel id, navigate to the customer
        if (customers.data.length) {
          this.navController.navigateRoot(`/dashboard/customers/${customers.data[0].id}`);
        } else {
          this.navController.navigateRoot(`/dashboard/customers`);
        }
      } else {
        this.msgSrvc.show(`Your channels are being connected. You will be notified when it is complete.`, {
          duration: 5000,
        });
        this.navController.navigateRoot(`/dashboard/customers`);
      }

      await loading.dismiss();
    } catch (err) {
      this.msgSrvc.show(err.message);
    } finally {
      this.isLoading = false;
    }
  }

  handleFilter() {
    const term = this.searchText?.toLowerCase();

    this.filteredChannels = this.unconnectedChannels.filter((channel) => {
      let passSearch = !term;
      let passFilter =
        (this.isFilteringSharedChannels && channel.isShared) || (!this.isFilteringSharedChannels && !channel.isShared);

      if (term) {
        passSearch = channel.name.toLowerCase().includes(term);
      }

      return passSearch && passFilter;
    });
  }

  onSelect({ selected }) {
    this.selectedChannels.splice(0, this.selectedChannels.length);
    this.selectedChannels.push(...selected);
  }

  async connectUserToSlack() {
    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    this.slackService.userOauth(
      `${window.location.origin}/close-window`,
      window.location.origin,
      !this.connectedTeams.find((team) => team.connectionId === this.selectedTeam.connectionId).isPrimary,
    );

    this.isLoading = true;

    await delay(5000);

    while (!this.userConnectedToSlack) {
      await this.loadChannels();
      await delay(2000);
    }

    this.isConnecting = false;
    this.isLoading = false;
  }

  connectTeamToSlack() {
    this.slackService.oauth(
      window.location.href,
      window.location.origin,
      !(this.connectedTeams.find((team) => team.connectionId === this.selectedTeam.connectionId)?.isPrimary ?? true),
    );
  }

  openHelpdesk() {
    window.open('https://docs.unthread.io/account-setup/adding-unthread', '_blank');
  }

  goBack() {
    this.navController.navigateRoot('dashboard/home');
  }

  toggleRowSelected(row: any) {
    this.selectedChannels.push(row);
  }

  selectProject() {
    this.searchablePopoverService.projects({
      event,
      showNoneOption: false,
      selectedProjectId: this.project?.id,
      callback: async (_id: string, _name: string, object: any) => {
        this.setProject(object);
      },
    });
  }
}
